import React, { useEffect, useMemo, useState } from 'react';
import {
  Award,
  BriefcaseBusiness,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Filter,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import '../Employees/Employees.css';
import { getDesignations } from '../hrisApi';

const statusClass = (value) => String(value || 'Active').toLowerCase().replace(/\s+/g, '-');

const toJobTitleRow = (record, index) => ({
  id: record.id || record.designationCode || record.designationName || `job-title-${index}`,
  name: record.designationName || record.title || record.name || record.jobTitle || '—',
  code: record.designationCode || record.code || '—',
  department: record.departmentName || record.department || '—',
  grade: record.grade || record.level || '—',
  employeeCount: Number(record.employeeCount || record.employees || 0),
  status: record.status || 'Active',
  description: record.description || '—',
});

function Status({ value }) {
  return <span className={`employee-status ${statusClass(value)}`}><i />{value}</span>;
}

const JobTitle = () => {
  const [jobTitles, setJobTitles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [department, setDepartment] = useState('All departments');
  const [grade, setGrade] = useState('All grades');
  const [status, setStatus] = useState('All statuses');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const [showFilters, setShowFilters] = useState(true);

  const load = () => {
    setLoading(true);
    setError(null);
    getDesignations({ page: 0, size: 500 })
      .then((result) => {
        const rows = (result.content || []).map(toJobTitleRow);
        setJobTitles(rows);
      })
      .catch(() => setError('Could not load job titles from the server.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const unique = (key) => [...new Set(jobTitles.map((item) => item[key]).filter((value) => value && value !== '—'))];

  const filtered = useMemo(() => jobTitles.filter((item) => {
    const haystack = `${item.name} ${item.code} ${item.department} ${item.grade} ${item.description}`.toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (department === 'All departments' || item.department === department)
      && (grade === 'All grades' || item.grade === grade)
      && (status === 'All statuses' || item.status === status);
  }), [jobTitles, query, department, grade, status]);

  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const activeFilters = [department !== 'All departments', grade !== 'All grades', status !== 'All statuses'].filter(Boolean).length;
  const totalEmployees = jobTitles.reduce((sum, item) => sum + item.employeeCount, 0);

  const reset = () => {
    setQuery('');
    setDepartment('All departments');
    setGrade('All grades');
    setStatus('All statuses');
    setPage(1);
  };

  const select = (event, setter) => {
    setter(event.target.value);
    setPage(1);
  };

  const changeRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  return (
    <div className="employee-directory-page hris-master-page">
      <header className="employee-page-header">
        <div>
          <div className="eyebrow"><BriefcaseBusiness size={15} /> HUMAN RESOURCES</div>
          <h1>Job Title</h1>
          <p>Directory jabatan untuk melihat mapping department, grade, status, dan jumlah karyawan.</p>
        </div>
        <div className="source-state">
          <span><i /> {loading ? 'Loading…' : error ? 'Offline' : 'Live data'}</span>
          <small>{error || 'Synced from HRIS backend'}</small>
        </div>
      </header>

      <section className="employee-summary-grid">
        <article><span className="summary-icon teal"><BriefcaseBusiness size={21} /></span><div><small>Total job titles</small><strong>{jobTitles.length}</strong></div></article>
        <article><span className="summary-icon blue"><Building2 size={21} /></span><div><small>Departments</small><strong>{unique('department').length}</strong></div></article>
        <article><span className="summary-icon purple"><Award size={21} /></span><div><small>Grades</small><strong>{unique('grade').length}</strong></div></article>
        <article><span className="summary-icon orange"><Users size={21} /></span><div><small>Employees mapped</small><strong>{totalEmployees}</strong></div></article>
      </section>

      <section className="employee-filter-panel">
        <div className="filter-panel-head">
          <div>
            <h2>Job title directory</h2>
            <p>Gunakan pencarian dan filter untuk menemukan jabatan yang sesuai.</p>
          </div>
          <button className="filter-toggle" onClick={() => setShowFilters((value) => !value)}>
            <Filter size={16} /> Filters {activeFilters > 0 && <b>{activeFilters}</b>}
          </button>
        </div>
        <div className="search-row">
          <label className="employee-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
              placeholder="Search job title, code, department, grade..."
            />
          </label>
          <button className="clear-filter" onClick={reset}>Clear</button>
          <button className="refresh-button" onClick={load} title="Refresh from server"><RefreshCw size={17} /> Refresh</button>
        </div>
        {showFilters && (
          <div className="filter-row">
            <label>Department<select value={department} onChange={(event) => select(event, setDepartment)}><option>All departments</option>{unique('department').map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Grade<select value={grade} onChange={(event) => select(event, setGrade)}><option>All grades</option>{unique('grade').map((value) => <option key={value}>{value}</option>)}</select></label>
            <label>Status<select value={status} onChange={(event) => select(event, setStatus)}><option>All statuses</option>{unique('status').map((value) => <option key={value}>{value}</option>)}</select></label>
          </div>
        )}
      </section>

      <section className="employee-table-panel">
        <div className="directory-heading">
          <div>
            <h2>Job Titles</h2>
            <p>{filtered.length} job title{filtered.length === 1 ? '' : 's'} shown</p>
          </div>
          <span className="sync-note"><Clock3 size={15} /> Synced from HRIS backend</span>
        </div>
        <div className="table-scroll">
          <table className="employee-table hris-master-table">
            <thead>
              <tr>
                <th>Job title</th>
                <th>Department</th>
                <th>Grade</th>
                <th>Employees</th>
                <th>Status</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {visible.length ? visible.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="name-cell">
                      <span className="department-avatar"><BriefcaseBusiness size={17} /></span>
                      <span><strong>{item.name}</strong><small>{item.code}</small></span>
                    </div>
                  </td>
                  <td><span className="department-pill">{item.department}</span></td>
                  <td>{item.grade}</td>
                  <td><span className="assignment-count"><Users size={15} /> {item.employeeCount}</span></td>
                  <td><Status value={item.status} /></td>
                  <td>{item.description}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="empty-state">{loading ? 'Loading job titles…' : 'Tidak ada job title yang sesuai dengan filter.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>Showing {filtered.length ? ((page - 1) * rowsPerPage) + 1 : 0}-{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length} job titles</span>
          <div className="pagination-controls">
            <label>Rows per page<select value={rowsPerPage} onChange={changeRowsPerPage}><option value={5}>5</option><option value={7}>7</option><option value={10}>10</option><option value={25}>25</option></select></label>
            <div className="pager">
              <button disabled={page === 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
              <b>{page}</b>
              <button disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default JobTitle;
